/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes } from '@adobe-commerce/elsie/lib/classes';
import { lazy, Suspense, SVGProps } from 'preact/compat';
import { useState, useEffect } from 'preact/hooks';

import '@adobe-commerce/elsie/components/Icon/Icon.css';

export type IconType = keyof typeof import('@adobe-commerce/elsie/icons');

const lazyIcons = {
  Add: lazy(() => import('@adobe-commerce/elsie/icons/Add.svg')),
  AddressBook: lazy(() => import('@adobe-commerce/elsie/icons/AddressBook.svg')),
  Bulk: lazy(() => import('@adobe-commerce/elsie/icons/Bulk.svg')),
  Burger: lazy(() => import('@adobe-commerce/elsie/icons/Burger.svg')),
  Business: lazy(() => import('@adobe-commerce/elsie/icons/Business.svg')),
  Card: lazy(() => import('@adobe-commerce/elsie/icons/Card.svg')),
  Cart: lazy(() => import('@adobe-commerce/elsie/icons/Cart.svg')),
  Check: lazy(() => import('@adobe-commerce/elsie/icons/Check.svg')),
  CheckWithCircle: lazy(() => import('@adobe-commerce/elsie/icons/CheckWithCircle.svg')),
  ChevronDown: lazy(() => import('@adobe-commerce/elsie/icons/ChevronDown.svg')),
  ChevronRight: lazy(() => import('@adobe-commerce/elsie/icons/ChevronRight.svg')),
  ChevronUp: lazy(() => import('@adobe-commerce/elsie/icons/ChevronUp.svg')),
  Close: lazy(() => import('@adobe-commerce/elsie/icons/Close.svg')),
  Coupon: lazy(() => import('@adobe-commerce/elsie/icons/Coupon.svg')),
  Date: lazy(() => import('@adobe-commerce/elsie/icons/Date.svg')),
  Delivery: lazy(() => import('@adobe-commerce/elsie/icons/Delivery.svg')),
  Edit: lazy(() => import('@adobe-commerce/elsie/icons/Edit.svg')),
  EmptyBox: lazy(() => import('@adobe-commerce/elsie/icons/EmptyBox.svg')),
  Eye: lazy(() => import('@adobe-commerce/elsie/icons/Eye.svg')),
  EyeClose: lazy(() => import('@adobe-commerce/elsie/icons/EyeClose.svg')),
  Gift: lazy(() => import('@adobe-commerce/elsie/icons/Gift.svg')),
  GiftCard: lazy(() => import('@adobe-commerce/elsie/icons/GiftCard.svg')),
  Heart: lazy(() => import('@adobe-commerce/elsie/icons/Heart.svg')),
  HeartFilled: lazy(() => import('@adobe-commerce/elsie/icons/HeartFilled.svg')),
  InfoFilled: lazy(() => import('@adobe-commerce/elsie/icons/InfoFilled.svg')),
  List: lazy(() => import('@adobe-commerce/elsie/icons/List.svg')),
  Locker: lazy(() => import('@adobe-commerce/elsie/icons/Locker.svg')),
  Minus: lazy(() => import('@adobe-commerce/elsie/icons/Minus.svg')),
  Order: lazy(() => import('@adobe-commerce/elsie/icons/Order.svg')),
  OrderError: lazy(() => import('@adobe-commerce/elsie/icons/OrderError.svg')),
  OrderSuccess: lazy(() => import('@adobe-commerce/elsie/icons/OrderSuccess.svg')),
  PaymentError: lazy(() => import('@adobe-commerce/elsie/icons/PaymentError.svg')),
  Placeholder: lazy(() => import('@adobe-commerce/elsie/icons/Placeholder.svg')),
  PlaceholderFilled: lazy(
    () => import('@adobe-commerce/elsie/icons/PlaceholderFilled.svg')
  ),
  Purchase: lazy(() => import('@adobe-commerce/elsie/icons/Purchase.svg')),
  Quote: lazy(() => import('@adobe-commerce/elsie/icons/Quote.svg')),
  Search: lazy(() => import('@adobe-commerce/elsie/icons/Search.svg')),
  SearchFilled: lazy(() => import('@adobe-commerce/elsie/icons/SearchFilled.svg')),
  Sort: lazy(() => import('@adobe-commerce/elsie/icons/Sort.svg')),
  Star: lazy(() => import('@adobe-commerce/elsie/icons/Star.svg')),
  Structure: lazy(() => import('@adobe-commerce/elsie/icons/Structure.svg')),
  Team: lazy(() => import('@adobe-commerce/elsie/icons/Team.svg')),
  Trash: lazy(() => import('@adobe-commerce/elsie/icons/Trash.svg')),
  User: lazy(() => import('@adobe-commerce/elsie/icons/User.svg')),
  View: lazy(() => import('@adobe-commerce/elsie/icons/View.svg')),
  Wallet: lazy(() => import('@adobe-commerce/elsie/icons/Wallet.svg')),
  Warning: lazy(() => import('@adobe-commerce/elsie/icons/Warning.svg')),
  WarningFilled: lazy(() => import('@adobe-commerce/elsie/icons/WarningFilled.svg')),
  WarningWithCircle: lazy(
    () => import('@adobe-commerce/elsie/icons/WarningWithCircle.svg')
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'size'> {
  source?:
  | FunctionComponent<SVGProps<SVGSVGElement> & { title?: string }>
  | IconType
  | string;
  size?: '12' | '16' | '24' | '32' | '64' | '80';
  stroke?: '1' | '2' | '3' | '4';
  className?: string;
  title?: string;
}

export type IconNode = FunctionComponent<
  SVGProps<SVGSVGElement> & { title?: string }
>;

function isValidUrl(source: string): boolean { // check for URL from same domain
  try {
    if (source.startsWith('//')) {
      const absoluteUrl = `${window.location.protocol}${source}`;
      const url = new URL(absoluteUrl);
      if (url.hostname !== window.location.hostname) {
        console.error(
          `[Icon] External URL rejected for security: ${source} - Only same-domain URLs are allowed`
        );
        return false;
      }
      return true;
    }
    const url = new URL(source);
    
    if (url.hostname !== window.location.hostname) {
      console.error(`[Icon] External URL rejected for security: ${source} - Only same-domain URLs are allowed`);
      return false;
    }
    
    return true;
  } catch {
    console.error(`[Icon] Invalid URL format: ${source}`);
    return false;
  }
}

function UrlSvgLoader({
  url,
  ...props
}: SVGProps<SVGSVGElement> & { url: string }) {
  const [svgContent, setSvgContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          console.error(`[Icon] Failed to fetch SVG: ${response.status} ${response.statusText}`);
          throw error;
        }
        return response.text();
      })
      .then(content => {
        // Check if content is valid SVG
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, "image/svg+xml");
          const svg = doc.querySelector('svg');
          if (!svg) {
            throw new Error("No <svg> element found");
          }
          // Success!
        } catch(e: unknown) {
          if (e instanceof Error ) {
            console.error(`[Icon] Invalid SVG content from ${url}: ${e.message}`);
          } else {
            console.error(`[Icon] Invalid SVG content from ${url}: ${String(e)}`);
          }
          setError(true);
          setLoading(false);
        }
        

        // Process SVG content to ensure proper sizing and accessibility
        let processedContent = content;

        if (props.width) {
          processedContent = processedContent.replace(
            /<svg([^>]*)\s+width\s*=\s*["'][^"']*["']/gi,
            '<svg$1'
          );
          processedContent = processedContent.replace(
            /<svg/i,
            `<svg width="${props.width}"`
          );
        }

        if (props.height) {
          processedContent = processedContent.replace(
            /<svg([^>]*)\s+height\s*=\s*["'][^"']*["']/gi,
            '<svg$1'
          );
          processedContent = processedContent.replace(
            /<svg/i,
            `<svg height="${props.height}"`
          );
        }
        
        if (props.title) {
          processedContent = processedContent.replace(/<title[^>]*>.*?<\/title>/gi, '');
          processedContent = processedContent.replace(
            /<svg([^>]*)>/i,
            `<svg$1><title>${props.title}</title>`
          );
        }
        
        setSvgContent(processedContent);
        setLoading(false);
      })
      .catch((error) => {
        if (error instanceof Error) {
          console.error(`[Icon] ${error.message}`);
        } else {
          console.error(`[Icon] ${String(error)}`);
        }
        setError(true);
        setLoading(false);
      });
  }, [url, props.width, props.height, props.title]);

  if (loading || error) {
    return <svg {...props} />;
  }

  return (
    <span
      className={props.className}
      style={{
        width: String(props.width),
        height: String(props.height),
        display: 'inline-flex',
        lineHeight: 0,
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}

export function Icon({
  source: Source,
  size = '24',
  stroke = '2',
  viewBox = '0 0 24 24',
  className,
  ...props
}: IconProps) {

  const defaultProps = {
    className: classes([
      'dropin-icon',
      `dropin-icon--shape-stroke-${stroke}`,
      className,
    ]),
    width: size,
    height: size,
    viewBox,
  };

  // Only validate strings that look like URLs (start with http, //, or /)
  const isLikelyUrl =
    typeof Source === 'string' &&
    (Source.startsWith('http') ||
      Source.startsWith('//') ||
      Source.startsWith('/'));

  if (isLikelyUrl && isValidUrl(Source)) {
    return (
      <Suspense fallback={<svg {...props} {...defaultProps} />}>
        <UrlSvgLoader url={Source} {...props} {...defaultProps}/>
      </Suspense>
    );
  }

  const LazyIcon = typeof Source === 'string' && Source in lazyIcons
    ? lazyIcons[Source as IconType]
    : null;

  const isRejectedUrl = isLikelyUrl && !isValidUrl(Source);

  return (
    <Suspense fallback={<svg {...props} {...defaultProps} />}>
      {LazyIcon ? (
        <LazyIcon {...props} {...defaultProps} />
      ) : isRejectedUrl ? (
        <svg {...props} {...defaultProps} />
      ) : (
        // @ts-ignore
        <Source {...props} {...defaultProps} />
      )}
    </Suspense>
  );
}
